import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface PromotionalConfig {
  isEnabled: boolean;
  couponCode: string;
  description: string;
  discountPercentage: number;
  startDate: Date;
  endDate: Date;
  showHeroBanner: boolean;
  showStickyHeader: boolean;
  showFloatingWidget: boolean;
  showInlinePromotions: boolean;
  urgencyMessage: string;
  urgencyText: string;
  customMessage?: string;
}

interface PromotionalContextType {
  config: PromotionalConfig;
  updateConfig: (updates: Partial<PromotionalConfig>) => void;
  resetToDefaults: () => void;
  isActive: boolean;
  timeRemaining: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
}

const getEnvConfig = (): PromotionalConfig => {
  // Debug logging for production builds to troubleshoot Azure
  if (import.meta.env.PROD) {
    console.log('🔍 PRODUCTION - All env vars:', import.meta.env);
    console.log('🔍 PRODUCTION - VITE_PROMO_ENABLED:', import.meta.env.VITE_PROMO_ENABLED);
  }
  
  // Helper function to parse boolean environment variables
  const parseEnvBoolean = (value: string | undefined, defaultValue: boolean): boolean => {
    if (value === undefined) return defaultValue;
    return value.toLowerCase() === 'true';
  };

  // Helper function to parse date environment variables
  const parseEnvDate = (value: string | undefined, defaultDate: Date): Date => {
    if (value === undefined) return defaultDate;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? defaultDate : parsed;
  };

  // Default fallback dates
  const defaultStartDate = new Date();
  const defaultEndDate = (() => {
    const date = new Date();
    date.setDate(date.getDate() + 5);
    return date;
  })();

  return {
    isEnabled: parseEnvBoolean(import.meta.env.VITE_PROMO_ENABLED, true),
    couponCode: import.meta.env.VITE_PROMO_COUPON_CODE || 'AI_COURSE_50_OFF',
    description: import.meta.env.VITE_PROMO_DESCRIPTION || 'Save 50% on all AI Testing courses!',
    discountPercentage: parseInt(import.meta.env.VITE_PROMO_DISCOUNT_PERCENTAGE || '50'),
    startDate: parseEnvDate(import.meta.env.VITE_PROMO_START_DATE, defaultStartDate),
    endDate: parseEnvDate(import.meta.env.VITE_PROMO_END_DATE, defaultEndDate),
    showHeroBanner: parseEnvBoolean(import.meta.env.VITE_PROMO_SHOW_HERO_BANNER, true),
    showStickyHeader: parseEnvBoolean(import.meta.env.VITE_PROMO_SHOW_STICKY_HEADER, true),
    showFloatingWidget: parseEnvBoolean(import.meta.env.VITE_PROMO_SHOW_FLOATING_WIDGET, true),
    showInlinePromotions: parseEnvBoolean(import.meta.env.VITE_PROMO_SHOW_INLINE_PROMOTIONS, true),
    urgencyMessage: import.meta.env.VITE_PROMO_URGENCY_MESSAGE || '🔥 LEVEL UP SKILL OFFER! 🔥',
    urgencyText: import.meta.env.VITE_PROMO_URGENCY_TEXT || '⚡ Only 5 days left! Don\'t miss out!',
    customMessage: import.meta.env.VITE_PROMO_CUSTOM_MESSAGE || undefined,
  };
};

const defaultConfig: PromotionalConfig = getEnvConfig();

const PromotionalContext = createContext<PromotionalContextType | undefined>(undefined);

export const usePromotional = (): PromotionalContextType => {
  const context = useContext(PromotionalContext);
  if (!context) {
    throw new Error('usePromotional must be used within a PromotionalProvider');
  }
  return context;
};

interface PromotionalProviderProps {
  children: ReactNode;
}

export const PromotionalProvider: React.FC<PromotionalProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<PromotionalConfig>(() => {
    // In production, prioritize environment variables
    // In development, allow localStorage overrides for admin panel testing
    // BUT always respect environment variables when explicitly set to false
    const isProduction = import.meta.env.PROD;
    const envConfig = getEnvConfig();
    
    if (isProduction) {
      // Production: Use environment variables only
      return envConfig;
    } else {
      // Development: Allow localStorage overrides for admin panel
      // BUT respect environment variables when isEnabled is explicitly false
      if (envConfig.isEnabled === false) {
        // If environment explicitly disables promotion, ignore localStorage
        return envConfig;
      }
      
      const saved = localStorage.getItem('promotional_config');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          return {
            ...envConfig,
            ...parsed,
            startDate: new Date(parsed.startDate || envConfig.startDate),
            endDate: new Date(parsed.endDate || envConfig.endDate),
          };
        } catch {
          return envConfig;
        }
      }
      return envConfig;
    }
  });

  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  // Calculate if promotion is currently active
  const isActive = React.useMemo(() => {
    if (!config.isEnabled) return false;
    const now = new Date();
    return now >= config.startDate && now <= config.endDate;
  }, [config.isEnabled, config.startDate, config.endDate]);

  // Update time remaining
  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date().getTime();
      const end = config.endDate.getTime();
      const difference = end - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeRemaining({ days, hours, minutes, seconds });
      } else {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);
    return () => clearInterval(interval);
  }, [config.endDate]);

  // Save to localStorage whenever config changes (development only)
  useEffect(() => {
    const isProduction = import.meta.env.PROD;
    if (!isProduction) {
      localStorage.setItem('promotional_config', JSON.stringify(config));
    }
  }, [config]);

  const updateConfig = (updates: Partial<PromotionalConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const resetToDefaults = () => {
    setConfig(defaultConfig);
    localStorage.removeItem('promotional_config');
  };

  const value: PromotionalContextType = {
    config,
    updateConfig,
    resetToDefaults,
    isActive,
    timeRemaining,
  };

  return (
    <PromotionalContext.Provider value={value}>
      {children}
    </PromotionalContext.Provider>
  );
};

export default PromotionalContext;